import { useEffect, useState } from 'react';
import { SlideOver } from '@/components/SlideOver';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Alert } from '@/components/Alert';
import { FormSection } from '@/components/FormSection';
import { Button } from '@/components/Button';
import type { UpdateJobInput, JobStatus } from '@maker-toolchest/shared';

interface UpdateJobStatusFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateJobInput) => Promise<void>;
  currentStatus: JobStatus;
  loading?: boolean;
  error?: string | null;
}

const statusOptions = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'READY', label: 'Ready' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

export function UpdateJobStatusForm({
  open,
  onClose,
  onSubmit,
  currentStatus,
  loading = false,
  error,
}: UpdateJobStatusFormProps) {
  const [status, setStatus] = useState(currentStatus);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    if (open) {
      setStatus(currentStatus);
      setStatusMessage('');
    }
  }, [open, currentStatus]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data: UpdateJobInput = {
      status: status as JobStatus,
    };

    if (statusMessage.trim()) {
      data.statusMessage = statusMessage.trim();
    }

    await onSubmit(data);
  };

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Update Job Status"
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="update-job-status-form" disabled={loading} loading={loading}>
            Update Status
          </Button>
        </>
      }
    >
      <form id="update-job-status-form" onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="error" message={error} />
        )}

        <FormSection title="Job Status" description="Update the status of this job">
            <div className="space-y-4">
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-foreground mb-1">
                  Status *
                </label>
                <Select
                  id="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as JobStatus)}
                  options={statusOptions}
                  disabled={loading}
                />
              </div>

              <div>
                <label htmlFor="statusMessage" className="block text-sm font-medium text-foreground mb-1">
                  Status Message
                </label>
                <Input
                  id="statusMessage"
                  type="text"
                  value={statusMessage}
                  onChange={(e) => setStatusMessage(e.target.value)}
                  placeholder="Optional message about the status change"
                  disabled={loading}
                />
              </div>
            </div>
          </FormSection>
        </form>
    </SlideOver>
  );
}
